import { Box, Flex, Tag, TagLeftIcon, Text, Button, VStack, useColorModeValue } from '@chakra-ui/react';
import { TrendingUp, Sparkles } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { WinExcited, User } from '../../types';

interface WinsExcitedCardProps {
  entry: WinExcited & { user?: User };
  currentUserId: string;
  onDelete: () => void;
}

/**
 * Helper function to convert Timestamp or Date to Date
 */
function toDate(value: Timestamp | Date | string): Date {
  if (value instanceof Date) {
    return value;
  }
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate();
  }
  return new Date(value as string);
}

/**
 * Wins & Excited Card Component
 * Displays a single win or excited entry with delete functionality
 */
export function WinsExcitedCard({ entry, currentUserId, onDelete }: WinsExcitedCardProps) {
  const isWin = entry.type === 'win';
  const canDelete = currentUserId === entry.userId;

  // Color mode values for dark mode support
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColorLight = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box
      borderLeftWidth="4px"
      borderLeftColor={isWin ? 'green.500' : 'purple.500'}
      shadow="sm"
      borderRadius="md"
      p={4}
      bg={cardBg}
    >
      <Flex justify="space-between" align="flex-start" gap={4}>
        {/* Left Side - Content */}
        <VStack align="start" spacing={2} flex={1}>
          <Tag
            colorScheme={isWin ? 'green' : 'purple'}
            variant="subtle"
            size="sm"
          >
            <TagLeftIcon as={isWin ? TrendingUp : Sparkles} />
            {isWin ? 'Win' : 'Excited'}
          </Tag>
          <Text fontSize="md" mt={2}>
            {entry.content}
          </Text>
        </VStack>

        {/* Right Side - User Info and Actions */}
        <VStack align="end" spacing={2}>
          <Text fontSize="sm" fontWeight="medium">
            {entry.user?.name || 'Unknown User'}
          </Text>
          <Text fontSize="xs" color={textColorLight}>
            {toDate(entry.createdAt).toLocaleString()}
          </Text>
          {canDelete && (
            <Button
              size="xs"
              colorScheme="red"
              variant="ghost"
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
        </VStack>
      </Flex>
    </Box>
  );
}

